import { TCase } from '@/features/cases/case/api/types/case.type';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { TOrganizationChannel } from '@/features/channels/_shared/api/get-organization-channel.api';
import { TLabelCase } from '@/features/cases/label-case/types/label-case.type';
import { TAgent } from '@/features/users/agent/types/agent.type';
import { TTeam } from '@/features/users/team/types/team.type';
import { TTabCaseQuery } from '../hooks/use-tabcase-qs';
import { TUserStatesStore } from '@/features/user/stores/user.store';
import {
  calculateTotalFilterCases,
  countCase,
  countSelectedCases,
  filterByTabCaseAndRole,
  filterCases,
  sortCases,
} from '../utils/case.util';

export const defaultFilterCase: IFilterCase = {
  labelCases: [],
  channels: [],
  agents: [],
  teams: [],
  searchCustomerOrGroupName: '',
};

export type TSelectedMenuCaseMode =
  | 'resolved'
  | 'unassign'
  | 'assign-to-me'
  | 'assign-to-other'
  | 'select-label'
  | 'idle';
export interface IFilterCase {
  labelCases?: TLabelCase[];
  channels?: TOrganizationChannel[];
  agents?: TAgent[];
  teams?: TTeam[];
  searchCustomerOrGroupName?: string;
}

interface TCasesStore {
  // ===== STATE =====
  selectedMenuCaseMode: TSelectedMenuCaseMode;
  selectedCase: TCase | null;
  selectedMessage: TCase | null;
  selectedCases: TCase[] | null;
  listActiveCase: TCase[];
  listUnassignCase: TCase[];
  listResolvedCase: TCase[];
  filterResolvedCase: IFilterCase;
  pendingFilterResolvedCase: IFilterCase;
  filterActiveCase: IFilterCase;
  pendingFilterActiveCase: IFilterCase;
  filterUnassignCase: IFilterCase;
  pendingFilterUnassignCase: IFilterCase;
  isLoadingFromWebsocket: boolean;
  /** Bumped when case unread is updated (e.g. via socket) so UI can re-render and show fresh count. */
  unreadVersion: number;

  // ===== ACTIONS =====
  actions: {
    setSelectedMenuCaseMode: (mode: TSelectedMenuCaseMode) => void;
    setSelectedCase: (selectedCase: TCase | null) => void;
    setSelectedMessage: (selectedMessage: TCase | null) => void;
    setSelectedCases: (selectedCases: TCase[]) => void;
    toggleSelectedCase: (selectedCase: TCase) => void;

    setListActiveCase: (listActiveCase: TCase[]) => void;
    setListUnassignCase: (listUnassignCase: TCase[]) => void;
    setListResolvedCase: (listResolvedCase: TCase[]) => void;

    setFilterResolvedCase: (filterResolvedCase: IFilterCase) => void;
    setFilterActiveCase: (filterActiveCase: IFilterCase) => void;
    setFilterUnassignCase: (filterCase: IFilterCase) => void;

    setPendingFilterResolvedCase: (pendingFilterResolvedCase: IFilterCase) => void;
    setPendingFilterActiveCase: (pendingFilterActiveCase: IFilterCase) => void;
    setPendingFilterUnassignCase: (pendingFilterUnassignCase: IFilterCase) => void;

    applyPendingFilterActiveCase: () => void;
    resetPendingFilterActiveCase: () => void;
    resetPendingFilterUnassignCase: () => void;
    applyPendingFilterUnassignCase: () => void;
    applyPendingFilterResolvedCase: () => void;
    resetPendingFilterResolvedCase: () => void;

    setResetSelectedCases: () => void;
    setResetSelectedCase: () => void;
    setResetSelectedMenuCaseMode: () => void;

    setIsLoadingFromWebsocket: (isLoading: boolean) => void;
    bumpUnreadVersion: () => void;
  };
}

export const casesStore = create<TCasesStore>((set, get) => ({
  // ===== STATE =====
  selectedMenuCaseMode: 'idle',
  selectedCase: null,
  selectedCases: null,
  selectedMessage: null,
  listActiveCase: [],
  listUnassignCase: [],
  listResolvedCase: [],
  filterResolvedCase: defaultFilterCase,
  filterActiveCase: defaultFilterCase,
  filterUnassignCase: defaultFilterCase,
  pendingFilterResolvedCase: defaultFilterCase,
  pendingFilterActiveCase: defaultFilterCase,
  pendingFilterUnassignCase: defaultFilterCase,
  isLoadingFromWebsocket: true,
  unreadVersion: 0,

  // ===== ACTIONS =====
  actions: {
    setSelectedMenuCaseMode: (mode: TSelectedMenuCaseMode) => {
      set({ selectedMenuCaseMode: mode });
    },
    setSelectedCase: (selectedCase: TCase | null) => {
      set({ selectedCase: selectedCase });
    },
    setSelectedMessage: (selectedMessage: TCase | null) => {
      set({ selectedMessage: selectedMessage });
    },
    setSelectedCases: (selectedCases: TCase[]) => {
      set({ selectedCases: selectedCases });
    },
    toggleSelectedCase: (selectedCase: TCase) => {
      const currentState = get();
      const currentSelectedCases = currentState.selectedCases || [];

      // PERFORMANCE OPTIMIZATION: Use Set for O(1) lookup instead of O(n) some()
      const selectedIds = new Set(currentSelectedCases.map((c) => c.conversationId));
      const caseExists = selectedIds.has(selectedCase.conversationId);

      let updatedCases: TCase[] | null = null;

      if (caseExists) {
        // Remove if already exists
        updatedCases = currentSelectedCases.filter((c) => c.conversationId !== selectedCase.conversationId);
      } else {
        // Add if doesn't exist
        updatedCases = [...currentSelectedCases, selectedCase];
      }

      // If no cases left, set to null
      if (updatedCases.length === 0) {
        updatedCases = null;
      }
      set({ selectedCases: updatedCases });
    },
    setListActiveCase: (listActiveCase: TCase[]) => set({ listActiveCase: listActiveCase }),
    setListUnassignCase: (listUnassignCase: TCase[]) => set({ listUnassignCase: listUnassignCase }),
    setListResolvedCase: (listResolvedCase: TCase[]) => set({ listResolvedCase: listResolvedCase }),
    setFilterResolvedCase: (filterResolvedCase: IFilterCase) => set({ filterResolvedCase: filterResolvedCase }),
    setFilterActiveCase: (filterActiveCase: IFilterCase) => set({ filterActiveCase: filterActiveCase }),
    setFilterUnassignCase: (filterUnassignCase: IFilterCase) => set({ filterUnassignCase: filterUnassignCase }),
    setPendingFilterResolvedCase: (pendingFilterResolvedCase: IFilterCase) =>
      set({ pendingFilterResolvedCase: pendingFilterResolvedCase }),
    setPendingFilterActiveCase: (pendingFilterActiveCase: IFilterCase) =>
      set({ pendingFilterActiveCase: pendingFilterActiveCase }),
    setPendingFilterUnassignCase: (pendingFilterUnassignCase: IFilterCase) =>
      set({ pendingFilterUnassignCase: pendingFilterUnassignCase }),
    applyPendingFilterActiveCase: () => {
      const state = get();
      set({ filterActiveCase: state.pendingFilterActiveCase });
    },
    resetPendingFilterActiveCase: () => {
      const state = get();
      set({ pendingFilterActiveCase: state.filterActiveCase });
    },

    resetPendingFilterUnassignCase: () => {
      const state = get();
      set({ pendingFilterUnassignCase: state.filterUnassignCase });
    },
    applyPendingFilterUnassignCase: () => {
      const state = get();
      set({ filterUnassignCase: state.pendingFilterUnassignCase });
    },
    applyPendingFilterResolvedCase: () => {
      const state = get();
      set({ filterResolvedCase: state.pendingFilterResolvedCase });
    },
    resetPendingFilterResolvedCase: () => {
      const state = get();
      set({ pendingFilterResolvedCase: state.filterResolvedCase });
    },
    setResetSelectedCases: () => set({ selectedCases: null }),
    setResetSelectedCase: () => set({ selectedCase: null }),
    setResetSelectedMenuCaseMode: () => set({ selectedMenuCaseMode: 'idle' }),

    setIsLoadingFromWebsocket: (isLoading: boolean) => set({ isLoadingFromWebsocket: isLoading }),
    bumpUnreadVersion: () => set((s) => ({ unreadVersion: s.unreadVersion + 1 })),
  },
}));

// ===== SELECTORS =====
export const useUnreadVersion = () => casesStore((state) => state.unreadVersion);
export const useSelectedMenuCaseMode = () => casesStore(useShallow((state) => state.selectedMenuCaseMode));
export const useSelectedMessage = () => casesStore(useShallow((state) => state.selectedMessage));
export const useSelectedCase = () => casesStore(useShallow((state) => state.selectedCase));
export const useSelectedCases = () => casesStore(useShallow((state) => state.selectedCases));

// ===== LIST CASE =====
// export const useListActiveCase = () =>
//   casesStore(useShallow((state) => filterCases(state.listActiveCase, state.filterActiveCase)));
// export const useListResolvedCase = () =>
//   casesStore(useShallow((state) => filterCases(state.listResolvedCase, state.filterResolvedCase)));

export const useFilteredListActiveCaseByTab = (tabCase: TTabCaseQuery, user: TUserStatesStore) =>
  casesStore(
    useShallow((state) => {
      if (!state.listActiveCase?.length) return [];
      const filteredCases = filterCases(state.listActiveCase, state.filterActiveCase);
      const sortedCases = sortCases(filteredCases);
      return filterByTabCaseAndRole(sortedCases, tabCase, user);
    })
  );

export const useFilteredListUnassignCaseByTab = () =>
  casesStore(
    useShallow((state) => {
      if (!state.listUnassignCase?.length) return [];
      const filteredCases = filterCases(state.listUnassignCase, state.filterUnassignCase);
      const sortedCases = sortCases(filteredCases);
      return sortedCases;
    })
  );

// ===== FILTER CASE =====
// ===== FILTER RESOLVED CASE =====
export const useFilterResolvedCase = () => casesStore(useShallow((state) => state.filterResolvedCase));
export const useTotalFilterResolvedCase = () =>
  casesStore(
    useShallow((state) => {
      const filterResolvedCase = state.filterResolvedCase;
      const totalFilterResolvedCase = calculateTotalFilterCases(filterResolvedCase);
      return totalFilterResolvedCase;
    })
  );

// ===== FILTER ACTIVE CASE =====
export const useFilterActiveCase = () => casesStore(useShallow((state) => state.filterActiveCase));
export const useTotalFilterActiveCase = () =>
  casesStore(
    useShallow((state) => {
      const filterActiveCase = state.filterActiveCase;
      const totalFilterActiveCase = calculateTotalFilterCases(filterActiveCase);
      return totalFilterActiveCase;
    })
  );

// ===== FILTER UNASSIGN CASE =====
export const useFilterUnassignCase = () => casesStore(useShallow((state) => state.filterUnassignCase));
export const useTotalFilterUnassignCase = () =>
  casesStore(
    useShallow((state) => {
      const filterUnassignCase = state.filterUnassignCase;
      const totalFilterUnassignCase = calculateTotalFilterCases(filterUnassignCase);
      return totalFilterUnassignCase;
    })
  );

// ===== COUNT CASE =====
// export const useCountSelectedActiveCases = () =>
//   casesStore(
//     useShallow((state) => {
//       // PERFORMANCE OPTIMIZATION: Memoize filtered cases to avoid recalculating
//       const filteredCases = filterAndSortCases(state.listActiveCase, state.filterActiveCase);
//       return countSelectedCases(filteredCases, state.selectedCases);
//     })
//   );
// export const useCountSelectedResolvedCases = () =>
//   casesStore(
//     useShallow((state) => {
//       // PERFORMANCE OPTIMIZATION: Memoize filtered cases to avoid recalculating
//       const filteredCases = filterAndSortCases(state.listResolvedCase, state.filterResolvedCase);
//       return countSelectedCases(filteredCases, state.selectedCases);
//     })
//   );

// COUNT CASE BY TAB
export const useCountActiveCaseByTab = (user: TUserStatesStore) =>
  casesStore(
    useShallow((state) => {
      const listActiveCase = state.listActiveCase;
      const filterActiveCase = state.filterActiveCase;

      if (!listActiveCase?.length) return { myCase: 0, teamCase: 0, allCase: 0 };

      return countCase(listActiveCase, user, filterActiveCase, 'activeCase');
    })
  );

export const useCountUnassignCaseByTab = (user: TUserStatesStore) =>
  casesStore(
    useShallow((state) => {
      const listUnassignCase = state.listUnassignCase;
      const filterUnassignCase = state.filterUnassignCase;

      if (!listUnassignCase?.length) return { myCase: 0, teamCase: 0, allCase: 0 };

      return countCase(listUnassignCase, user, filterUnassignCase, 'unassignCase');
    })
  );

// export const useCountResolvedCaseByTab = (user: TUserStatesStore) =>
//   casesStore(
//     useShallow((state) => {
//       if (!state.listResolvedCase?.length) return { myCase: 0, teamCase: 0, allCase: 0 };
//       return countCase(state.listResolvedCase, user, state.filterResolvedCase, 'resolvedCase');
//     })
//   );

// COUNT SELECTED CASE BY TAB
export const useCountSelectedActiveCaseByTab = (tabCase: TTabCaseQuery, user: TUserStatesStore) =>
  casesStore(
    useShallow((state) => {
      const listActiveCase = state.listActiveCase;
      const selectedCases = state.selectedCases;
      const filterActiveCase = state.filterActiveCase;
      const filteredActiveCases = filterCases(listActiveCase, filterActiveCase);
      const filteredActiveCasesByTabAndUserRole = filterByTabCaseAndRole(filteredActiveCases, tabCase, user);
      const countedSelectedCases = countSelectedCases(filteredActiveCasesByTabAndUserRole, selectedCases);
      return countedSelectedCases;
    })
  );

export const useCountSelectedUnassignCaseByTab = () =>
  casesStore(
    useShallow((state) => {
      const listUnassignCase = state.listUnassignCase;
      const selectedCases = state.selectedCases;
      const filterUnassignCase = state.filterUnassignCase;
      const filteredUnassignCases = filterCases(listUnassignCase, filterUnassignCase);
      const countedSelectedCases = countSelectedCases(filteredUnassignCases, selectedCases);
      return countedSelectedCases;
    })
  );

// IS ALL SELECTED CASE BY TAB
export const useIsAllSelectedActiveCaseByTab = (tabCase: TTabCaseQuery, user: TUserStatesStore) =>
  casesStore(
    useShallow((state) => {
      const listActiveCase = state.listActiveCase;
      const selectedCases = state.selectedCases;
      const filterActiveCase = state.filterActiveCase;
      const filteredActiveCases = filterCases(listActiveCase, filterActiveCase);
      const filteredActiveCasesByTabAndUserRole = filterByTabCaseAndRole(filteredActiveCases, tabCase, user);
      const countedSelectedCases = countSelectedCases(filteredActiveCasesByTabAndUserRole, selectedCases);
      return countedSelectedCases === filteredActiveCasesByTabAndUserRole.length;
    })
  );

// IS ALL SELECTED UNASSIGN CASE BY TAB
export const useIsAllSelectedUnassignCaseByTab = () =>
  casesStore(
    useShallow((state) => {
      const listUnassignCase = state.listUnassignCase;
      const selectedCases = state.selectedCases;
      const filterUnassignCase = state.filterUnassignCase;
      const filteredUnassignCases = filterCases(listUnassignCase, filterUnassignCase);
      const countedSelectedCases = countSelectedCases(filteredUnassignCases, selectedCases);
      return countedSelectedCases === filteredUnassignCases.length;
    })
  );

// ===== WEBSOCKET LOADING STATE =====
export const useIsLoadingFromWebsocket = () => casesStore(useShallow((state) => state.isLoadingFromWebsocket));

// ===== ACTIONS =====
export const useCasesActions = () => casesStore(useShallow((state) => state.actions));
